import React from 'react';
import { useDispatch } from 'react-redux';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';

import { executeStored } from 'app/main/components/Utils';
import useSettings from 'app/main/components/hooks/useSettings';

interface MaterialSearchSelectProps {
	name: string;
	systable: string;
	value?: string;
	sendChange: (value: string | null) => void;
	disabled: boolean;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root': {
		padding: 0,
		'& > .MuiInputBase-input': {
			padding: 8,
			paddingRight: 32
		}
	}
}));

/** A component that can show a list of items and allows the user to find */
function MaterialSearchSelect({ name, systable, value, sendChange, disabled }: MaterialSearchSelectProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(systable);

	const idName = `${settings.modu_short}_id`;

	const [options, setOptions] = React.useState([]);
	const [isLoading, setIsLoading] = React.useState(true);

	const selectedOption = options.find(opt => opt[idName] === value) || null;

	const getOptionSelected = (option, fieldValue) => option[idName] === fieldValue[idName];

	function getTitle(option) {
		return option[`${settings.modu_short}_title`];
	}

	function handleChange(_event, selectedValue) {
		if (selectedValue) {
			sendChange(selectedValue[idName]);
		} else {
			sendChange(null);
		}
	}

	React.useEffect(() => {
		setIsLoading(true);
		executeStored({
			action: 'st_sys_systable_list',
			params: { systable },
			dispatch,
			successHandler: response => {
				const { list } = response.data;
				setOptions(list || []);
				setIsLoading(false);
			}
		});
	}, [dispatch, idName, systable]);

	return (
		<StyledAutocomplete
			renderInput={params => (
				<TextField
					{...params}
					name={name}
					variant="outlined"
					size="small"
					fullWidth
					autoComplete="off"
					InputLabelProps={{
						...params.InputLabelProps,
						shrink: true
					}}
				/>
			)}
			autoComplete={false}
			options={options}
			value={selectedOption}
			onChange={handleChange}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			disabled={disabled}
			getOptionLabel={getTitle}
			isOptionEqualToValue={getOptionSelected}
			disableCloseOnSelect
			autoHighlight
			selectOnFocus={false}
			disableClearable
			fullWidth
		/>
	);
}

export default MaterialSearchSelect;
